import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Upload, CheckCircle2, AlertCircle, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PublicLayout } from '@/layouts/PublicLayout';
import {
  careerPageApi,
  type PublicJobResponse,
} from '@/lib/career-page-api';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ALLOWED_EXTENSIONS = ['.pdf', '.docx'];
const MAX_COVER_LETTER = 1000;

/** Map API status codes to user-friendly pt-BR messages */
function getErrorMessage(err: unknown): string {
  const e = err as { statusCode?: number; message?: string };
  if (e.statusCode === 409) return 'Você já se candidatou a esta vaga recentemente.';
  if (e.statusCode === 429) return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
  if (e.statusCode === 404) return 'Vaga não encontrada ou não está aceitando candidaturas.';
  return e.message || 'Ocorreu um erro. Tente novamente.';
}

/** Simple email validation */
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function CareerApplyPage() {
  const { orgSlug, jobId } = useParams<{ orgSlug: string; jobId: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Job info
  const [jobData, setJobData] = useState<PublicJobResponse | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [coverLetter, setCoverLetter] = useState('');
  const [honeypot, setHoneypot] = useState('');

  // Upload state
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [s3Key, setS3Key] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!orgSlug || !jobId) return;
    careerPageApi
      .getPublicJob(orgSlug, jobId)
      .then((res) => {
        setJobData(res);
        document.title = `Candidatar-se — ${res.job.title} | ${res.organization.name}`;
      })
      .catch(() => setPageError(true))
      .finally(() => setPageLoading(false));
  }, [orgSlug, jobId]);

  // File selection + upload
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileError('');
    setUploadStatus('idle');
    setS3Key('');

    // Validate extension
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setFileError('Formato não suportado. Use PDF ou DOCX.');
      return;
    }

    // Validate type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setFileError('Formato não suportado. Use PDF ou DOCX.');
      return;
    }

    // Validate size
    if (file.size > MAX_FILE_SIZE) {
      setFileError('Arquivo muito grande. Máximo 10MB.');
      return;
    }

    setFileName(file.name);
    setUploadStatus('uploading');
    setUploadProgress(0);

    try {
      const { uploadUrl, s3Key: key } = await careerPageApi.getUploadUrl(
        orgSlug!,
        jobId!,
        file.name,
        file.type,
      );

      // Upload to S3 with progress via XMLHttpRequest
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload failed')));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(file);
      });

      setS3Key(key);
      setUploadStatus('done');
    } catch {
      setUploadStatus('error');
      setFileError('Falha no envio do currículo. Tente novamente.');
    }
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgSlug || !jobId) return;

    setSubmitError('');
    setSubmitting(true);

    try {
      await careerPageApi.submitApplication(orgSlug, jobId, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        coverLetter: coverLetter.trim() || undefined,
        resumeS3Key: s3Key,
        honeypot,
      });
      setSuccess(true);
    } catch (err) {
      setSubmitError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid =
    name.trim().length >= 2 &&
    isValidEmail(email.trim()) &&
    uploadStatus === 'done' &&
    s3Key !== '';

  // --- Render ---

  if (pageLoading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PublicLayout>
    );
  }

  if (pageError || !jobData) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-muted-foreground text-lg">
            Vaga não encontrada ou não está aceitando candidaturas.
          </p>
        </div>
      </PublicLayout>
    );
  }

  const { job, organization } = jobData;

  // Success screen
  if (success) {
    return (
      <PublicLayout primaryColor={organization.primaryColor}>
        <div className="container mx-auto px-4 py-16 max-w-lg text-center">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Candidatura enviada com sucesso!</h1>
          <p className="text-muted-foreground mb-6">
            Verifique seu email para confirmação.
          </p>
          <Link
            to={`/careers/${orgSlug}`}
            className="text-primary hover:underline text-sm"
          >
            ← Voltar para vagas
          </Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout primaryColor={organization.primaryColor}>
      <div className="container mx-auto px-4 py-8 max-w-lg">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-6">
          <Link to={`/careers/${orgSlug}`} className="hover:text-foreground transition-colors">
            Vagas em {organization.name}
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to={`/careers/${orgSlug}/${jobId}`} className="hover:text-foreground transition-colors truncate">
            {job.title}
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground">Candidatar-se</span>
        </nav>

        <h1 className="text-2xl font-bold mb-1">{job.title}</h1>
        <p className="text-muted-foreground text-sm mb-6">{organization.name}</p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot — hidden from humans */}
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            className="hidden"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Seu nome completo"
              maxLength={100}
              required
            />
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              required
            />
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <Label htmlFor="phone">Telefone</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(11) 99999-9999"
            />
          </div>

          {/* Resume Upload */}
          <div className="space-y-1.5">
            <Label>Currículo * (PDF ou DOCX, máx. 10MB)</Label>
            <div
              className="border-2 border-dashed rounded-md p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileChange}
                className="hidden"
              />
              {uploadStatus === 'idle' && (
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <Upload className="h-6 w-6" />
                  <span className="text-sm">Clique para selecionar arquivo</span>
                </div>
              )}
              {uploadStatus === 'uploading' && (
                <div className="space-y-2">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                  <p className="text-sm text-muted-foreground">Enviando currículo... {uploadProgress}%</p>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-primary h-1.5 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
              {uploadStatus === 'done' && (
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="text-sm">✓ Currículo enviado — {fileName}</span>
                </div>
              )}
              {uploadStatus === 'error' && (
                <div className="flex items-center justify-center gap-2 text-destructive">
                  <AlertCircle className="h-5 w-5" />
                  <span className="text-sm">Falha no envio. Clique para tentar novamente.</span>
                </div>
              )}
            </div>
            {fileError && (
              <p className="text-sm text-destructive">{fileError}</p>
            )}
          </div>

          {/* Cover Letter */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="coverLetter">Carta de apresentação</Label>
              <span className="text-xs text-muted-foreground">
                {coverLetter.length}/{MAX_COVER_LETTER}
              </span>
            </div>
            <Textarea
              id="coverLetter"
              value={coverLetter}
              onChange={(e) => {
                if (e.target.value.length <= MAX_COVER_LETTER) {
                  setCoverLetter(e.target.value);
                }
              }}
              placeholder="Conte um pouco sobre você e por que se interessa por esta vaga..."
              rows={5}
            />
          </div>

          {/* Error */}
          {submitError && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-md p-3">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {submitError}
            </div>
          )}

          {/* Submit */}
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={!isFormValid || submitting}
            style={{ backgroundColor: 'var(--career-primary, hsl(var(--primary)))' }}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Enviando...
              </>
            ) : (
              'Enviar Candidatura'
            )}
          </Button>
        </form>
      </div>
    </PublicLayout>
  );
}
